import { Button, Card, useToast } from '@citizen-walk/shared-ui';
import type {
  AiVerificationTemplate,
  VerificationTemplatePreset,
} from '@citizen-walk/shared-types';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAdminAuth } from '../auth';
import { useDistrict } from '../districts-admin';
import { useVerificationTemplatePresets } from '../masters';
import { VerificationTemplateImportFromPresetModal } from './VerificationTemplateImportFromPresetModal';
import { copyPresetToDistrict, linkPresetToDistrict } from './useTemplateCopyLink';
import {
  createVerificationTemplate,
  duplicateVerificationTemplate,
  setVerificationTemplateStatus,
  useVerificationTemplates,
  type VerificationTemplateListItem,
} from './useVerificationTemplates';

/**
 * AI 検証テンプレ一覧画面 (admin 第 23 弾、Phase 5 A-S19)。
 *
 * 旧 `old/apps/admin/src/features/verification-admin/TemplatesPage.tsx` を v2 callable 不要・
 * Firestore 直接 CRUD で minimum 踏襲。本 sprint scope は minimum (一覧 + 作成 + 状態切替 + 編集 link)。
 * A-S18 (ダッシュボード) / A-S21〜23 (配分セット / 結果) / master 連携 / duplicate は別 sprint。
 *
 * 認可: master / district admin (担当地区) のみ操作可。rules で deny される (UI でも guard)。
 */

type TemplateStatus = AiVerificationTemplate['status'];

const STATUS_LABEL: Record<TemplateStatus, string> = {
  draft: '下書き',
  published: '配信中',
  archived: 'アーカイブ',
};

const STATUS_BADGE: Record<TemplateStatus, string> = {
  draft: 'bg-slate-300 text-slate-800',
  published: 'bg-success-600 text-white',
  archived: 'bg-slate-500 text-white',
};

/** quick toggle 用: draft → published → archived → draft を循環。 */
function nextStatus(s: TemplateStatus): TemplateStatus {
  if (s === 'draft') return 'published';
  if (s === 'published') return 'archived';
  return 'draft';
}

export function VerificationTemplatesAdminPage() {
  const { districtId } = useParams<{ districtId: string }>();
  const navigate = useNavigate();
  const { state: authState } = useAdminAuth();
  const districtState = useDistrict(districtId ?? null);
  const templatesState = useVerificationTemplates(districtId ?? null);
  const presetsState = useVerificationTemplatePresets();
  const { toast } = useToast();

  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  // Sprint BG-DataSettings 13 — Master preset 取込 modal state (GAP-A11)
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importBusy, setImportBusy] = useState(false);

  const isMaster = authState.role === 'master';
  const isDistrictAdminOfThis =
    authState.role === 'district' && !!districtId && authState.districts.includes(districtId);
  const canManage = isMaster || isDistrictAdminOfThis;
  const uid = authState.user?.uid ?? '';

  async function handleCreate() {
    if (!districtId || !canManage) return;
    setCreating(true);
    try {
      const newId = await createVerificationTemplate(districtId, uid);
      toast('success', '検証テンプレを作成しました');
      navigate(`/districts/${districtId}/verification/templates/${newId}`);
    } catch (e) {
      toast('error', `作成失敗: ${(e as Error).message}`);
    } finally {
      setCreating(false);
    }
  }

  /** GAP-A11 (Sprint BE) — 検証テンプレを複製。新規 draft を作って編集ページへ。 */
  async function handleDuplicate(item: VerificationTemplateListItem) {
    if (!districtId || !canManage) return;
    setBusyId(item.id);
    try {
      const newId = await duplicateVerificationTemplate(districtId, item, uid);
      toast('success', '複製しました (draft として保存)');
      navigate(`/districts/${districtId}/verification/templates/${newId}`);
    } catch (e) {
      toast('error', `複製失敗: ${(e as Error).message}`);
    } finally {
      setBusyId(null);
    }
  }

  /** Sprint BG-DataSettings 13 (GAP-A11) — Master preset から copy で取込。 */
  async function handleCopyFromPreset(preset: VerificationTemplatePreset & { id: string }) {
    if (!districtId || !canManage) return;
    setImportBusy(true);
    try {
      const newId = await copyPresetToDistrict(districtId, preset, uid);
      toast('success', `「${preset.displayName}」を copy で取込みました。draft として保存。`);
      setImportModalOpen(false);
      navigate(`/districts/${districtId}/verification/templates/${newId}`);
    } catch (e) {
      toast('error', `copy 取込失敗: ${(e as Error).message}`);
    } finally {
      setImportBusy(false);
    }
  }

  /** Sprint BG-DataSettings 13 (GAP-A11) — Master preset から link で取込。 */
  async function handleLinkFromPreset(preset: VerificationTemplatePreset & { id: string }) {
    if (!districtId || !canManage) return;
    setImportBusy(true);
    try {
      const newId = await linkPresetToDistrict(districtId, preset, uid);
      toast('success', `「${preset.displayName}」を link で取込みました。draft として保存。`);
      setImportModalOpen(false);
      navigate(`/districts/${districtId}/verification/templates/${newId}`);
    } catch (e) {
      toast('error', `link 取込失敗: ${(e as Error).message}`);
    } finally {
      setImportBusy(false);
    }
  }

  async function handleToggleStatus(item: VerificationTemplateListItem) {
    if (!districtId || !canManage) return;
    const next = nextStatus(item.status);
    setBusyId(item.id);
    try {
      await setVerificationTemplateStatus(districtId, item.id, next, uid, item.version);
      toast('success', `「${item.displayName || '(無題)'}」を ${STATUS_LABEL[next]} にしました`);
    } catch (e) {
      toast('error', `状態変更失敗: ${(e as Error).message}`);
    } finally {
      setBusyId(null);
    }
  }

  const items = templatesState.status === 'ready' ? templatesState.items : [];

  // Sprint BG-Menu (第 2 段): list item に「テンプレ: <preset name>」バッジ表示用に
  // sourceMasterTemplateId → preset displayName のマップを作る (preset 一覧 ready 時のみ)。
  const presetNameById = (() => {
    if (presetsState.status !== 'ready') return new Map<string, string>();
    return new Map(presetsState.items.map((p) => [p.id, p.displayName]));
  })();

  return (
    <>
      <header className="mb-6">
        <Link
          to={districtId ? `/districts/${districtId}` : '/districts'}
          className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
        >
          ← 地区詳細
        </Link>
        <h1 className="text-2xl font-bold text-slate-900 mt-2">
          運用中の検証
          {districtState.status === 'ready' ? ` — ${districtState.district.name}` : ''}
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          REQ-4.3.20〜25 — AI 解析結果のユーザー検証テンプレ管理 (配分セット / 結果 /
          ダッシュボードは別 sprint)。
        </p>
      </header>

      {!canManage && (
        <Card padding="md" className="border-danger-600 mb-4">
          <p className="text-sm text-danger-700">
            この地区の検証テンプレ管理権限がありません (role: {authState.role ?? 'none'}、地区:{' '}
            {districtId})。
          </p>
        </Card>
      )}

      <Card padding="md" className="mb-6 flex items-center justify-between gap-3 flex-wrap">
        <span className="text-sm text-slate-700">
          テンプレ (検証テンプレ) から取込んで運用、または空の検証テンプレを新規作成します。
        </span>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="primary"
            onClick={() => setImportModalOpen(true)}
            disabled={!canManage || creating || importBusy}
            title="検証テンプレから copy / link で取込"
          >
            📥 テンプレから作成
          </Button>
          <Button
            variant="secondary"
            onClick={() => void handleCreate()}
            disabled={!canManage || creating}
            title="空の検証テンプレを新規作成"
          >
            {creating ? '作成中…' : '＋ 空から新規作成'}
          </Button>
        </div>
      </Card>

      {importModalOpen && (
        <VerificationTemplateImportFromPresetModal
          presets={presetsState.status === 'ready' ? presetsState.items : []}
          loading={presetsState.status === 'loading'}
          busy={importBusy}
          onCopy={(preset) => void handleCopyFromPreset(preset)}
          onLink={(preset) => void handleLinkFromPreset(preset)}
          onClose={() => setImportModalOpen(false)}
        />
      )}

      <h2 className="text-base font-bold text-slate-900 mb-3">テンプレート一覧</h2>

      {templatesState.status === 'loading' && (
        <Card padding="md">
          <p className="text-sm text-slate-600">読込中…</p>
        </Card>
      )}

      {templatesState.status === 'error' && (
        <Card padding="md" className="border-danger-600">
          <p className="text-sm text-danger-700">読込エラー: {templatesState.error.message}</p>
        </Card>
      )}

      {templatesState.status === 'ready' && items.length === 0 && (
        <Card padding="md">
          <p className="text-sm text-slate-600">まだ検証テンプレはありません。</p>
        </Card>
      )}

      {templatesState.status === 'ready' && items.length > 0 && (
        <ul className="grid grid-cols-1 gap-2">
          {items.map((t) => (
            <li key={t.id}>
              <Card padding="md" className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${STATUS_BADGE[t.status]}`}>
                      {STATUS_LABEL[t.status]}
                    </span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-700">
                      投稿全体 {t.perPostQuestions.length} 問
                    </span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-700">
                      分類行 {t.perClassificationQuestions.length} 問
                    </span>
                    {t.targetMode && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary-100 text-primary-700">
                        mode: {t.targetMode}
                      </span>
                    )}
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                      v{t.version}
                    </span>
                    {t.linkedToMaster && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-warning-600 text-white">
                        {t.linkedToMaster}
                      </span>
                    )}
                    {t.sourceMasterTemplateId && (
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded bg-warning-100 text-warning-700 border border-warning-300"
                        title={`元テンプレ: ${t.sourceMasterTemplateId}`}
                      >
                        テンプレ:{' '}
                        {presetNameById.get(t.sourceMasterTemplateId) ?? t.sourceMasterTemplateId}
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-sm font-bold text-slate-900 truncate">
                    {t.displayName || '(無題)'}
                  </p>
                  {t.description && (
                    <p className="mt-1 text-xs text-slate-600 line-clamp-2 whitespace-pre-wrap">
                      {t.description}
                    </p>
                  )}
                  <p className="mt-1 text-[11px] text-slate-400">
                    ID: <code>{t.id}</code> / 更新: {new Date(t.updatedAt).toLocaleString('ja-JP')}
                  </p>
                </div>
                {canManage && (
                  <div className="shrink-0 flex flex-col gap-1">
                    <Link
                      to={`/districts/${districtId}/verification/templates/${t.id}`}
                      className="text-xs text-primary-600 hover:text-primary-700 hover:underline text-center min-h-touch flex items-center justify-center px-3"
                    >
                      編集 →
                    </Link>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => void handleToggleStatus(t)}
                      disabled={busyId === t.id}
                      title={`次の状態: ${STATUS_LABEL[nextStatus(t.status)]}`}
                    >
                      → {STATUS_LABEL[nextStatus(t.status)]}
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => void handleDuplicate(t)}
                      disabled={busyId === t.id}
                      title="このテンプレを複製 (新規 draft として保存)"
                    >
                      📋 複製
                    </Button>
                  </div>
                )}
              </Card>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
